import Card from "@pathwright/ui/src/components/card/Card"
import CardLink from "@pathwright/ui/src/components/card/CardLink"
import useCardStackModal from "@pathwright/ui/src/components/card/useCardStackModal"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import OverlayTrigger from "@pathwright/ui/src/components/overlay/OverlayTrigger"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { useScreensizeContext } from "@pathwright/ui/src/components/ui/Screensize"
import Text from "@pathwright/ui/src/components/ui/Text"
import { media } from "@pathwright/ui/src/components/utils/styles"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import get from "lodash/get"
import styled from "styled-components"
import useCohortPermissions from "../../cohort/useCohortPermissions"
import {
  getCohortSectionUrlValues,
  getGroupCommunityUrl,
  getManageGroupCommunityUrl
} from "../../utils/urls"
import {
  FEATURE_ACTION_VIEW,
  FEATURE_PERMISSION_DISABLED,
  FEATURE_PERMISSION_EVERYONE
} from "../constants"
import { CONTEXTUAL_FEATURE_PERMISSION_QUERY } from "../graphql"
import { useFeatureUpdatePermission } from "../permissions"
import {
  featureKeyToParam,
  getFeatureInfo,
  getFeaturePermissionInfo
} from "../utils"
import FeatureVisibilityIndicator from "./FeatureVisibilityIndicator"

const Menu = ({ featureKey, featureAction, context }) => {
  const { t } = useTranslate()

  const cohortPerms = useCohortPermissions({
    cohortId: context && context.cohort_id
  })

  const cfpQuery = useQuery(CONTEXTUAL_FEATURE_PERMISSION_QUERY, {
    variables: {
      featureKey,
      featureAction,
      context
    }
  })

  const canUpdateAllCohorts = useFeatureUpdatePermission({
    featureKey,
    featureAction
  }).hasPermission

  const canUpdateThisCohort = useFeatureUpdatePermission({
    featureKey,
    featureAction,
    context
  }).hasPermission

  const cfp = get(cfpQuery, "data.contextualFeaturePermission")
  // loading, or possibly an error, regardless, safe to return null
  if (!cfp) return null
  // loading permissions
  if (canUpdateAllCohorts === null || canUpdateThisCohort === null) return null

  const { title, link } = getFeatureInfo(cfp.feature_key, t)
  const { icon, titleVerbose, descVerbose } = getFeaturePermissionInfo(
    featureKey,
    cfp.permission_in_context,
    t,
    {
      feature: title
    }
  )

  const urlValues = getCohortSectionUrlValues(window.location.pathname)
  const toCohortCommunity = cohortPerms.hasEditorLevelAccess
    ? getManageGroupCommunityUrl(context.resource_id, urlValues.cohortId)
    : getGroupCommunityUrl(urlValues)

  return (
    <StyledContainer>
      <header>
        <Pathicon icon={icon} />
        <Text.H3>{titleVerbose}</Text.H3>
        <Text.Body>{descVerbose}</Text.Body>
      </header>
      <CardLink
        icon="info-circle"
        label={t("Learn more about the {{ feature }} ", {
          feature: title
        })}
        href={link}
      />
      {canUpdateAllCohorts && (
        <CardLink
          icon="eye"
          label={t("Change visbility for all cohorts")}
          to={`/manage/school/features/${featureKeyToParam(cfp.feature_key)}/`}
        />
      )}
      {canUpdateThisCohort && (
        <CardLink
          icon="eye"
          label={t("Change visibility for this cohort")}
          to={`${toCohortCommunity}${featureKeyToParam(cfp.feature_key)}/`}
        />
      )}
    </StyledContainer>
  )
}

Menu.propTypes = {
  ...CONTEXTUAL_FEATURE_PERMISSION_QUERY._propTypes
}

const StyledContainer = styled.div`
  ${media.min.phone`
    padding: 1em;
    margin-left: 15px;
    border-radius: 20px;
    background-color: whitesmoke;
    box-shadow: 0 2px 5px 0 rgb(0, 0, 0, 0.1);

    &:before {
      content: "";
      width: 0;
      height: 0;
      border-right: 12px solid whitesmoke;
      border-bottom: 12px solid transparent;
      border-top: 12px solid transparent;
      position: absolute;
      left: -12px;
      bottom: calc(60px / 2);
      transform: translateY(50%);
      margin-left: 15px;
    }  
  `}

  header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    > * + * {
      margin-top: 0.5em;
    }

    .Pathicon {
      font-size: 2em;
    }

    > p {
      text-align: center;
    }
  }
`

const MenuCard = ({ card, ...rest }) => (
  <Card card={card}>
    <Menu {...rest} />
  </Card>
)

const FeatureVisibilityCard = ({ featureKey, featureAction, context }) => {
  const { cardStack, onOpenCardStack } = useCardStackModal({
    component: MenuCard,
    props: {
      featureKey,
      featureAction,
      context
    }
  })

  return (
    <>
      {cardStack}
      <Container>
        <FeatureVisibilityIndicator
          featureKey={featureKey}
          featureAction={featureAction}
          context={context}
          onClick={onOpenCardStack}
        />
      </Container>
    </>
  )
}

const FeatureVisibilityOverlay = ({ featureKey, featureAction, context }) => {
  const screensize = useScreensizeContext()

  const cfpQuery = useQuery(CONTEXTUAL_FEATURE_PERMISSION_QUERY, {
    variables: {
      featureKey,
      featureAction,
      context
    }
  })

  const permissionInContext = get(
    cfpQuery,
    "data.contextualFeaturePermission.permission_in_context"
  )
  // loading, or possibly an error, regardless, safe to return null
  if (!permissionInContext) return null
  // visibility is not limited if visible to everyone
  if (permissionInContext === FEATURE_PERMISSION_EVERYONE) return null
  // should never render component if feature is disabled, but just to be sure
  if (permissionInContext === FEATURE_PERMISSION_DISABLED) return null

  return screensize === "sm" ? (
    <FeatureVisibilityCard
      featureKey={featureKey}
      featureAction={featureAction}
      context={context}
    />
  ) : (
    <Container>
      <OverlayTrigger
        trigger="click"
        placement="right"
        alignment="bottom"
        overlay={() => (
          <Menu
            featureKey={featureKey}
            featureAction={featureAction}
            context={context}
          />
        )}
        closeOnTriggerClick={false}
        closeOnOverlayClick={false}
      >
        <FeatureVisibilityIndicator
          featureKey={featureKey}
          featureAction={featureAction}
          context={context}
        />
      </OverlayTrigger>
    </Container>
  )
}

FeatureVisibilityOverlay.displayName = "FeatureVisibilityOverlay"

FeatureVisibilityOverlay.propTypes = {
  ...CONTEXTUAL_FEATURE_PERMISSION_QUERY._propTypes
}

FeatureVisibilityOverlay.defaultProps = {
  featureAction: FEATURE_ACTION_VIEW
}

const Container = styled.div`
  position: fixed;
  bottom: 10px;
  left: 10px;
  z-index: 10000;
`

export default FeatureVisibilityOverlay
